import {
  getUserPrivateProfile,
  invalidateUserPrivateProfile,
} from "@/hooks/useUserPrivateProfile";
import { useAuth } from "@/hooks/useAuth";
import {
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";

export type UserPrivateSettings = Record<string, unknown>;

export function userPrivateSettingsQueryKey(userId: string | null | undefined) {
  return ["user_private_settings", userId] as const;
}

/**
 * Eine gemeinsame React-Query für user_private.settings – liest über die geteilte
 * `user_private`-Zeile (`useUserPrivateProfile`), verhindert dadurch parallele
 * identische Supabase-Requests beim Dashboard-Boot.
 */
export function useUserPrivateSettings(userId?: string | null) {
  const { user, authReady } = useAuth();
  const resolvedUserId = userId ?? user?.id ?? null;
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: userPrivateSettingsQueryKey(resolvedUserId),
    queryFn: async () =>
      (await getUserPrivateProfile(queryClient, resolvedUserId!)).settings,
    enabled: authReady && !!resolvedUserId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

export function invalidateUserPrivateSettings(
  queryClient: QueryClient,
  userId: string | null | undefined
): void {
  queryClient.invalidateQueries({
    queryKey: userPrivateSettingsQueryKey(userId),
  });
  // Geteilte user_private-Zeile ebenfalls invalidieren, sonst liefert
  // getUserPrivateProfile() (staleTime 5 min) noch die alten Settings.
  invalidateUserPrivateProfile(queryClient, userId);
}
